import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronsUpDown, Building2, Search, Check, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  loadAccountsCache,
  saveAccountsCache,
  type CachedAccount,
} from '../lib/impersonation';
import toast from 'react-hot-toast';

type Facility = {
  type: string;
  facility_name: string | null;
  location_id: string | null;
  status: string;
};

type Account = CachedAccount & {
  facilities?: Facility[];
};

type Props = {
  /** Compact styling for amber banner */
  variant?: 'banner' | 'toolbar';
};

function facilityLabel(facilities?: Facility[]): string {
  if (!facilities?.length) return '';
  const f = facilities[0];
  return f.facility_name || f.location_id || f.type || '';
}

function filterCached(accounts: Account[], q: string): Account[] {
  if (!q) return accounts;
  const needle = q.toLowerCase();
  return accounts.filter((a) => {
    const email = (a.email || '').toLowerCase();
    const fac = (a.facilities || [])
      .map((f) => [f.facility_name, f.location_id, f.type].filter(Boolean).join(' '))
      .join(' ')
      .toLowerCase();
    return email.includes(needle) || fac.includes(needle);
  });
}

async function fetchAccounts(
  q: string,
  accessToken: string,
  impersonationId?: string | null
): Promise<Account[]> {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  params.set('limit', '50');
  if (impersonationId) params.set('impersonation_id', impersonationId);

  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
  };
  if (impersonationId) {
    headers['x-impersonation-id'] = impersonationId;
  }

  const res = await fetch(`/.netlify/functions/admin-list-accounts?${params.toString()}`, {
    headers,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Fall back to session cache (populated while still superadmin)
    const cached = filterCached(loadAccountsCache(), q);
    if (cached.length > 0) {
      console.warn('[AccountSwitcher] API failed, using cache:', json?.error || res.status);
      return cached;
    }
    throw new Error(json?.error || `Failed to load accounts (${res.status})`);
  }
  const accounts: Account[] = json.accounts || [];
  // Refresh cache whenever API succeeds (as superadmin or via impersonation path)
  if (!q && accounts.length > 0) {
    saveAccountsCache(accounts);
  }
  return accounts;
}

export default function AccountSwitcher({ variant = 'toolbar' }: Props) {
  const { isSuperAdmin, impersonation, startImpersonation, switchImpersonation } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [q, setQ] = React.useState('');
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);

  const canUse = isSuperAdmin || !!impersonation;

  React.useEffect(() => {
    const t = window.setTimeout(() => setQ(search.trim()), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const { data: accounts = [], isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['account-switcher', q, impersonation?.impersonation_id || 'none'],
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) throw new Error('Not authenticated');
      return fetchAccounts(q, token, impersonation?.impersonation_id);
    },
    enabled: open && canUse,
    staleTime: 30_000,
  });

  const currentId = impersonation?.target_user_id || null;
  const label = impersonation?.target_email || 'Switch account';

  if (!canUse) return null;

  const onSelect = async (account: Account) => {
    if (!account.user_id || account.user_id === currentId) {
      setOpen(false);
      return;
    }
    if (!account.email) {
      toast.error('Account has no email');
      return;
    }

    setBusyId(account.user_id);
    try {
      if (impersonation?.impersonation_id) {
        await switchImpersonation({
          target_user_id: account.user_id,
          target_email: account.email,
          reason: `Switched account via switcher to ${account.email}`,
        });
      } else {
        await startImpersonation({
          target_user_id: account.user_id,
          target_email: account.email,
          reason: `Opened via account switcher: ${account.email}`,
        });
      }
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message || 'Could not switch account');
    } finally {
      setBusyId(null);
    }
  };

  const isBanner = variant === 'banner';

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={!!busyId}
        className={
          isBanner
            ? 'inline-flex items-center gap-1.5 max-w-[min(100vw-8rem,20rem)] min-h-[36px] px-2.5 py-1.5 rounded-md text-sm font-semibold bg-white/80 border border-amber-300 text-amber-950 hover:bg-white'
            : 'inline-flex items-center gap-2 max-w-xs min-h-[40px] px-3 py-1.5 rounded-md text-sm font-medium bg-white border border-gray-300 text-gray-800 hover:bg-gray-50 shadow-sm'
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Building2 className={`w-4 h-4 shrink-0 ${isBanner ? 'text-amber-700' : 'text-indigo-600'}`} />
        <span className="truncate">{busyId ? 'Switching…' : label}</span>
        <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-60" />
      </button>

      {open && (
        <div
          className={`absolute z-[60] mt-1 w-[min(100vw-2rem,22rem)] rounded-lg border shadow-lg bg-white ${
            isBanner ? 'left-0 border-amber-200' : 'right-0 border-gray-200'
          }`}
          role="listbox"
        >
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                autoFocus
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search email or facility..."
                className="w-full pl-8 pr-2 py-2 text-sm border border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto py-1">
            {isLoading || isFetching ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading accounts…
              </div>
            ) : isError ? (
              <p className="px-3 py-4 text-sm text-red-600">
                {(error as Error)?.message || 'Failed to load accounts'}
              </p>
            ) : accounts.length === 0 ? (
              <p className="px-3 py-4 text-sm text-gray-500">No accounts match.</p>
            ) : (
              accounts.map((account) => {
                const active = account.user_id === currentId;
                const fac = facilityLabel(account.facilities);
                return (
                  <button
                    key={account.user_id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    disabled={!!busyId || active}
                    onClick={() => onSelect(account)}
                    className={`w-full text-left px-3 py-2.5 text-sm flex items-start gap-2 hover:bg-indigo-50 disabled:opacity-60 ${
                      active ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <span className="mt-0.5 w-4 shrink-0">
                      {busyId === account.user_id ? (
                        <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                      ) : active ? (
                        <Check className="w-4 h-4 text-indigo-600" />
                      ) : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-gray-900 truncate">
                        {account.email || account.user_id}
                      </span>
                      {fac && (
                        <span className="block text-xs text-gray-500 truncate">{fac}</span>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
